import { useForm, useWatch } from 'react-hook-form';
import { PageSection, PageWrapper } from '@src/components';
import { ResponsiveModal } from '@src/components/ResponsiveModal';
import {
  useGetUserProfileQuery,
  useUpdateProfileMutation,
} from '@src/services/profile/profileApiSlice';
import { ArrowRight2, LockCircle, Trash } from 'iconsax-react';
import { useEffect, useMemo, useState } from 'react';
import { FormEmail, FormPhone, FormTextBox } from '@src/components/form';
import { useCheckResponse } from '@src/hooks';
import { ChangePassword, DeleteAccount, SetPassword } from './components';
import clsx from 'clsx';
import { Button } from 'antd';
import isEqual from 'lodash/isEqual';

interface ProfileFormValues {
  fullName: string;
  phoneNumber: string | null;
  email: string;
}

export function Profile() {
  const { t, checkResponse } = useCheckResponse();
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [initialValues, setInitialValues] = useState<ProfileFormValues | null>(
    null
  );

  const {
    data: profile,
    refetch: refetchUserProfile,
    isLoading: isProfileLoading,
  } = useGetUserProfileQuery();
  const [updateProfile, { isLoading }] = useUpdateProfileMutation();

  const {
    control,
    formState: { errors, isValid },
    handleSubmit,
    reset,
  } = useForm<ProfileFormValues>({ mode: 'onChange' });

  useEffect(() => {
    if (profile) {
      const values = {
        fullName: profile.fullName,
        phoneNumber: profile.phoneNumber,
        email: profile.email,
      };
      setInitialValues(values);
      reset(values);
    }
  }, [profile, reset]);

  const onSubmit = async (formData: ProfileFormValues) => {
    try {
      const response = await updateProfile({
        ...profile,
        fullName: formData.fullName,
        phoneNumber: formData.phoneNumber === '' ? null : formData.phoneNumber,
      });
      checkResponse(response, 'update', t('pages.profile.title'));
      refetchUserProfile();
    } catch (error) {
      checkResponse({
        error,
      });
    }
  };

  const handleCloseDeleteAccount = () => {
    setTimeout(() => {
      setIsDeleteOpen(false);
    }, 300);
  };

  const handleCloseChangePassword = () => {
    setTimeout(() => {
      setIsChangePasswordOpen(false);
    }, 300);
  };

  const fullName = useWatch({ control, name: 'fullName', defaultValue: '' });
  const phoneNumber = useWatch({
    control,
    name: 'phoneNumber',
    defaultValue: null,
  });
  const email = useWatch({ control, name: 'email', defaultValue: '' });

  const watchedValues = useMemo(
    () => ({ fullName, phoneNumber, email }),
    [fullName, phoneNumber, email]
  );

  function normalizeProfileValues(
    values: ProfileFormValues | Partial<ProfileFormValues> | null
  ) {
    if (!values) return { fullName: '', phoneNumber: null, email: '' };

    return {
      fullName:
        typeof values.fullName === 'string' ? values.fullName.trim() : '',
      phoneNumber: values.phoneNumber?.trim() || null,
      email: typeof values.email === 'string' ? values.email.trim() : '',
    };
  }

  const isSameAsInitial = useMemo(() => {
    if (!initialValues) return true;

    const normalizedWatched = normalizeProfileValues(watchedValues);
    const normalizedInitial = normalizeProfileValues(initialValues);

    return isEqual(normalizedWatched, normalizedInitial);
  }, [watchedValues, initialValues]);

  const isButtonDisabled = !isValid || isSameAsInitial;

  return (
    <PageWrapper loading={isProfileLoading} title={t('pages.profile.title')}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <PageSection className='bg-wl-background text-wl-on-background p-4 md:p-6'>
          <h2 className='text-M-20 mb-4 md:mb-6'>Profile Info</h2>
          <PageSection className='bg-wl-surface mb-4 md:mb-6'>
            <div className='grid sm:grid-flow-row sm:grid-cols-2 gap-6 px-4 py-6'>
              <div
                className={clsx(
                  '[&_.ant-form-item-required]:!text-wl-on-background',
                  '[&_.ant-input]:!bg-wl-background [&_.ant-input]:text-wl-on-background'
                )}
              >
                <FormTextBox
                  t={t}
                  control={control}
                  name='fullName'
                  label='forms.yourName'
                  error={errors.fullName}
                  onlyLetters
                />
              </div>
              <div
                className={clsx(
                  '[&_.ant-form-item-required]:!text-wl-on-background',
                  '[&_.ant-input]:!bg-wl-background [&_.ant-input]:text-wl-on-background'
                )}
              >
                <FormPhone
                  t={t}
                  control={control}
                  name='phoneNumber'
                  label='forms.yourPhoneNumber'
                  error={errors.phoneNumber}
                />
              </div>
              <div
                className={clsx(
                  '[&_.ant-form-item-required]:!text-wl-on-background',
                  '[&_.ant-input]:!bg-wl-background [&_.ant-input]:text-wl-on-background'
                )}
              >
                <FormEmail
                  t={t}
                  control={control}
                  name='email'
                  label='forms.yourEmailAddress'
                  error={errors.email}
                  disabled
                />
              </div>
            </div>
          </PageSection>
          <div className='flex justify-start sm:w-full md:w-fit'>
            <Button
              size='large'
              className='btn-primary w-full px-10 py-6 text-B-16'
              htmlType='submit'
              loading={isLoading}
              disabled={isButtonDisabled}
            >
              {t('buttons.saveChanges')}
            </Button>
          </div>
        </PageSection>
      </form>
      <Button
        size='large'
        type='text'
        className='btn-secondary !bg-wl-background !text-wl-on-background rounded-xl border-none w-full flex items-center justify-between mt-6 py-8 !px-6'
        onClick={() => setIsChangePasswordOpen(true)}
      >
        <div className='flex items-center'>
          <LockCircle variant='Bulk' className='w-8 h-8 mr-2' />
          <span className='mr-auto text-M-16'>Password</span>
        </div>
        <ArrowRight2 className='text-lg  text-wl-primary' />
      </Button>

      <Button
        size='large'
        className='btn-danger mt-6 text-B-16 py-6 px-10 border-none'
        onClick={() => setIsDeleteOpen(true)}
      >
        Delete account
      </Button>
      <ResponsiveModal
        isOpen={isChangePasswordOpen}
        title={
          profile?.isSocialOnly
            ? t('pages.setPassword.title')
            : t('pages.changePassword.title')
        }
        headerIcon={<LockCircle variant='Bulk' />}
        content={
          profile?.isSocialOnly ? (
            <SetPassword onClose={() => setIsChangePasswordOpen(false)} />
          ) : (
            <ChangePassword onClose={() => setIsChangePasswordOpen(false)} />
          )
        }
        onClose={handleCloseChangePassword}
      />
      <ResponsiveModal
        isOpen={isDeleteOpen}
        title={t('buttons.deleteAccount')}
        headerIcon={<Trash variant='Bulk' />}
        content={<DeleteAccount onClose={() => setIsDeleteOpen(false)} />}
        onClose={handleCloseDeleteAccount}
      />
    </PageWrapper>
  );
}
